/** Shared Weatherglass / terrarium camera navigation. No scene updates or asset loads. */
export function createHabitatViewControls({THREE:T,OrbitControls,camera,element,scene,onChange=()=>{},habitatScale=10}){
  const controls=new OrbitControls(camera,element),lifetime=new AbortController();
  let sourceTarget=new T.Vector3(),follow=true,baseNear=camera.near,defaultCamera;
  const targetOf=source=>Array.isArray(source.userData.target)?new T.Vector3(...source.userData.target):source.getWorldDirection(new T.Vector3()).add(source.position);
  controls.enablePan=true;controls.screenSpacePanning=true;controls.panSpeed=.65;
  controls.enableDamping=true;controls.dampingFactor=.045;controls.rotateSpeed=.45;controls.zoomSpeed=.8;
  controls.minPolarAngle=.05;controls.maxPolarAngle=Math.PI-.05;
  // Half a millimetre in metre-based habitats; substantially closer than Weatherglass.
  controls.minDistance=Math.max(.000001,habitatScale*.00005);
  function adjustClip(){
    const near=Math.max(.000001,Math.min(baseNear,camera.position.distanceTo(controls.target)*.02));
    if(camera.near!==near){camera.near=near;camera.updateProjectionMatrix();}
  }
  function change(){adjustClip();onChange();}
  controls.addEventListener('change',change);
  controls.resetToCamera=source=>{
    defaultCamera=source.clone();baseNear=source.near;follow=true;
    camera.copy(source);sourceTarget.copy(targetOf(source));controls.target.copy(sourceTarget);
    const distance=camera.position.distanceTo(controls.target);
    controls.maxDistance=Math.max(distance,Math.min(source.far*.65,distance*4));
    // Drain residual damping before restoring the exact authored view.
    const damping=controls.enableDamping;controls.enableDamping=false;controls.update();
    camera.copy(source);controls.target.copy(sourceTarget);controls.enableDamping=damping;
    controls.update();adjustClip();controls.saveState();onChange();
  };
  controls.resetView=()=>controls.resetToCamera(defaultCamera);
  controls.syncTarget=source=>{
    if(follow){const next=targetOf(source),delta=next.clone().sub(sourceTarget);camera.position.add(delta);controls.target.add(delta);sourceTarget.copy(next);}
    controls.update();adjustClip();
  };
  controls.moveView=({azimuth=0,polar=0,zoom=1}={})=>{
    const spherical=new T.Spherical().setFromVector3(camera.position.clone().sub(controls.target));
    spherical.theta+=azimuth;spherical.phi=T.MathUtils.clamp(spherical.phi+polar,controls.minPolarAngle,controls.maxPolarAngle);
    spherical.radius=T.MathUtils.clamp(spherical.radius*zoom,controls.minDistance,controls.maxDistance);
    camera.position.copy(controls.target).add(new T.Vector3().setFromSpherical(spherical));controls.update();change();
  };
  controls.focusAt=(x,y)=>{
    const bounds=element.getBoundingClientRect();if(!bounds.width||!bounds.height)return false;
    const ray=new T.Raycaster();camera.updateMatrixWorld(true);scene.updateMatrixWorld(true);
    ray.setFromCamera(new T.Vector2((x-bounds.left)/bounds.width*2-1,-(y-bounds.top)/bounds.height*2+1),camera);
    const hit=ray.intersectObjects(scene.children,true).find(item=>{
      if(!item.object.isMesh)return false;
      for(let node=item.object;node;node=node.parent)if(!node.visible||node.userData.excludeCameraFocus||node.isHelper||/(glass|enclosure|helper|overlay)/i.test(node.name))return false;
      const material=Array.isArray(item.object.material)?item.object.material[item.face?.materialIndex??0]:item.object.material;
      return material?.visible!==false&&!(material?.transmission>.1)&&(!material?.transparent||material.opacity>=.8)&&item.distance>controls.minDistance&&Number.isFinite(item.point.lengthSq());
    });
    if(!hit)return false;
    follow=false;controls.target.copy(hit.point);controls.update();change();return true;
  };
  const options={signal:lifetime.signal};let down,lastTap;
  element.addEventListener('dblclick',event=>{if(controls.enabled){event.preventDefault();controls.focusAt(event.clientX,event.clientY);}},options);
  element.addEventListener('pointerdown',event=>{if(event.pointerType==='touch'){if(down)down=null;else down={id:event.pointerId,x:event.clientX,y:event.clientY,time:event.timeStamp};}},options);
  element.addEventListener('pointercancel',()=>{down=lastTap=null;},options);
  element.addEventListener('pointerup',event=>{
    if(event.pointerType!=='touch'||!down||down.id!==event.pointerId)return;
    const tap=down;down=null;
    if(event.timeStamp-tap.time>300||Math.hypot(event.clientX-tap.x,event.clientY-tap.y)>8){lastTap=null;return;}
    if(lastTap&&event.timeStamp-lastTap.time<350&&Math.hypot(event.clientX-lastTap.x,event.clientY-lastTap.y)<24){if(controls.enabled)controls.focusAt(event.clientX,event.clientY);lastTap=null;}
    else lastTap={x:event.clientX,y:event.clientY,time:event.timeStamp};
  },options);
  const dispose=controls.dispose.bind(controls);controls.dispose=()=>{lifetime.abort();controls.removeEventListener('change',change);dispose();};
  controls.resetToCamera(camera.clone());return controls;
}
